import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import Upload from "@/pages/UploadSetup";
import Interview from "@/pages/Interview";
import Prepare from "@/pages/Prepare";
import ReportAccessGate from "@/pages/ReportAccessGate";
import FreeComplete from "@/pages/FreeComplete";
import Downloads from "@/pages/Downloads";
import BetaFeedback from "@/pages/BetaFeedback";
import BetaReport from "@/pages/BetaReport";
import AdminBetaReports from "@/pages/AdminBetaReports";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import ProfileSetup from "@/pages/ProfileSetup";
import AccountSettings from "@/pages/AccountSettings";
import WaitingBeta from "@/pages/WaitingBeta";
import GoogleConsent from "@/pages/GoogleConsent";
import AdminHome from "@/pages/AdminHome";
import AdminBetaCodes from "@/pages/AdminBetaCodes";
import AdminBetaFeedback from "@/pages/AdminBetaFeedback";
import AdminUsers from "@/pages/AdminUsers";
import AdminAudit from "@/pages/AdminAudit";
import AdminPrepFailures from "@/pages/AdminPrepFailures";
import AdminBulkAudit from "@/pages/AdminBulkAudit";
import AdminPrivacyAudit from "@/pages/AdminPrivacyAudit";
import AdminVoiceCheck from "@/pages/AdminVoiceCheck";
import AdminLocalizationAudit from "@/pages/AdminLocalizationAudit";
import AdminTestScript from "@/pages/AdminTestScript";
import AdminResponsivePreview from "@/pages/AdminResponsivePreview";
import AdminStandardQuestions from "@/pages/AdminStandardQuestions";
import ProtectedRoute from "@/components/ProtectedRoute";
import SessionAllowanceTracker from "@/components/SessionAllowanceTracker";
import InterviewLifecycleControls from "@/components/InterviewLifecycleControls";
import WarmupPlaybackEnhancer from "@/components/WarmupPlaybackEnhancer";
import QuestionTranslationAid from "@/components/QuestionTranslationAid";
import GlobalHelpAccess from "@/components/HelpAccess";
import GlobalVolumeControl from "@/components/VolumeControl";
import HelpPanel from "@/components/HelpPanel";
import EnvironmentBanner from "@/components/EnvironmentBanner";
import LaunchScreen from "@/components/LaunchScreen";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  clearRememberedActiveInterviewRoute,
  getRememberedActiveInterviewRoute,
  getRememberedActiveInterviewSessionId,
  isInterviewDetourPath,
  rememberActiveInterviewRoute,
} from "@/lib/activeInterviewNavigation";

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (!user?.is_admin) return <Navigate to="/" replace />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  const [practiceFormat, setPracticeFormat] = useState(user?.is_admin ? "advanced" : null);
  const activeRoute = getRememberedActiveInterviewRoute();

  useEffect(() => {
    if (activeRoute || user?.is_admin) return undefined;
    let cancelled = false;

    api.get("/session-allowance")
      .then(({ data }) => {
        if (cancelled) return;
        setPracticeFormat(data?.access_tier === "free" ? "quick" : "advanced");
      })
      .catch(() => {
        if (!cancelled) setPracticeFormat("quick");
      });

    return () => {
      cancelled = true;
    };
  }, [activeRoute, user?.is_admin]);

  if (activeRoute) return <Navigate to={activeRoute} replace />;
  if (!practiceFormat) return null;
  return <Navigate to="/upload" replace state={{ practice_format: practiceFormat }} />;
}

import { Toaster } from "sonner";

/*
 * Google OAuth: the backend (/api/auth/google/callback) sets the session
 * cookie and then redirects the browser to `/auth/callback`, where
 * AuthCallback simply loads the session and hands over to ProtectedRoute.
 */

function AppRoutes() {
  const location = useLocation();
  const qaPreview = new URLSearchParams(location.search).get("qaPreview") === "1";
  const activePathMatch = location.pathname.match(/^\/(?:prepare|interview)\/([^/]+)$/);
  const currentActiveSessionId = activePathMatch?.[1] || null;
  const rememberedDetourSessionId = isInterviewDetourPath(location.pathname)
    ? getRememberedActiveInterviewSessionId()
    : null;
  const leaseSessionId = currentActiveSessionId || rememberedDetourSessionId;

  // The account menu, downloads and admin pages are allowed as temporary
  // detours during an interview. Remember the exact active route in this tab
  // so returning from one of those pages resumes the same session rather than
  // falling through to Interview Session Setup and tripping the concurrency
  // guard against our own interview.
  useEffect(() => {
    if (!currentActiveSessionId) return;
    rememberActiveInterviewRoute(location.pathname, location.search);
  }, [currentActiveSessionId, location.pathname, location.search]);

  // Once the interview reaches a terminal/report route there is nothing left
  // to resume from the account menu.
  useEffect(() => {
    if (/^\/report\/[^/]+$/.test(location.pathname) || location.pathname === "/free-complete" || location.pathname === "/login") {
      clearRememberedActiveInterviewRoute();
    }
  }, [location.pathname]);

  // Maintain a short server-side lease while a real Prepare/Interview page is
  // mounted OR while this same tab is temporarily visiting Account, Downloads
  // or Admin from that interview. This heartbeat deliberately does NOT count
  // as meaningful user activity and therefore cannot extend the 60-minute
  // inactivity limit. Its only purpose is crash recovery and preventing a
  // legitimate settings detour from looking like an orphaned interview.
  useEffect(() => {
    if (!leaseSessionId) return undefined;
    const heartbeatUrl = `/api/sessions/${encodeURIComponent(leaseSessionId)}/heartbeat`;
    let stopped = false;

    const sendHeartbeat = () => {
      if (stopped) return;
      fetch(heartbeatUrl, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
      }).catch(() => {
        // Loss of a heartbeat must never interrupt the current interview UI.
        // The server's 60-minute inactivity/TTL policy remains the hard fallback.
      });
    };

    sendHeartbeat();
    const timer = window.setInterval(sendHeartbeat, 20_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") sendHeartbeat();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", sendHeartbeat);

    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", sendHeartbeat);
    };
  }, [leaseSessionId]);

  // Best-effort immediate privacy cleanup for an unfinished interview when
  // the browser page is closed or genuinely left. Refresh/reload is excluded:
  // it must preserve the same working interview and let the remounted page
  // reconnect to it. Account/Downloads/Admin are temporary in-app detours, so
  // this protection follows the remembered interview there as well; closing
  // the browser while viewing settings still abandons the unfinished session.
  //
  // IMPORTANT: this deletes only temporary interview working data. It does
  // NOT reverse the interview charge/count recorded when the interview began.
  // The server-side 60-minute TTL remains the guaranteed final fallback when an
  // exit request cannot be delivered (power loss, process kill, network failure).
  // Admin Responsive Preview uses ?qaPreview=1. In that mode, navigating or
  // resizing the iframe must never abandon the session being inspected.
  useEffect(() => {
    if (!leaseSessionId || qaPreview) return undefined;
    const sessionId = leaseSessionId;
    const abandonUrl = `/api/sessions/${encodeURIComponent(sessionId)}/abandon`;
    let sent = false;
    let reloadInProgress = false;

    // Chromium's Navigation API identifies toolbar reloads, F5 and programmatic
    // reloads before beforeunload/pagehide fire. Keep the keyboard fallback for
    // browsers that do not expose Navigation API reload events.
    const markNavigationReload = (event) => {
      if (event?.navigationType === "reload") reloadInProgress = true;
    };
    const markKeyboardReload = (event) => {
      const key = String(event?.key || "").toLowerCase();
      if (key === "f5" || ((event?.ctrlKey || event?.metaKey) && key === "r")) {
        reloadInProgress = true;
      }
    };

    const navigationApi = window.navigation;
    if (navigationApi?.addEventListener) {
      navigationApi.addEventListener("navigate", markNavigationReload);
    }
    window.addEventListener("keydown", markKeyboardReload, true);

    const abandonOnPageExit = () => {
      if (reloadInProgress) return;
      if (
        window.__MEC_RESTARTING_SESSION__ === sessionId ||
        window.__MEC_ENDING_SESSION__ === sessionId
      ) {
        return;
      }
      if (sent) return;
      sent = true;

      try {
        fetch(abandonUrl, {
          method: "POST",
          credentials: "include",
          keepalive: true,
          cache: "no-store",
        }).catch(() => {
          try {
            if (navigator.sendBeacon) navigator.sendBeacon(abandonUrl);
          } catch { /* TTL fallback */ }
        });
      } catch {
        try {
          if (navigator.sendBeacon) navigator.sendBeacon(abandonUrl);
        } catch { /* TTL fallback */ }
      }
    };

    window.addEventListener("beforeunload", abandonOnPageExit);
    window.addEventListener("pagehide", abandonOnPageExit);
    return () => {
      if (navigationApi?.removeEventListener) {
        navigationApi.removeEventListener("navigate", markNavigationReload);
      }
      window.removeEventListener("keydown", markKeyboardReload, true);
      window.removeEventListener("beforeunload", abandonOnPageExit);
      window.removeEventListener("pagehide", abandonOnPageExit);
    };
  }, [leaseSessionId, qaPreview]);

  useEffect(() => {
    const match = location.pathname.match(/^\/report\/([^/]+)$/);
    if (!match || qaPreview) return undefined;
    const sessionId = match[1];
    const closeUrl = `/api/sessions/${encodeURIComponent(sessionId)}/close-repractice`;
    let sent = false;

    const closeRepracticeWindow = () => {
      if (sent) return;
      sent = true;
      try {
        fetch(closeUrl, {
          method: "POST",
          credentials: "include",
          keepalive: true,
          cache: "no-store",
        }).catch(() => {
          try {
            if (navigator.sendBeacon) navigator.sendBeacon(closeUrl);
          } catch { /* server-side auth/session enforcement is the backstop */ }
        });
      } catch {
        try {
          if (navigator.sendBeacon) navigator.sendBeacon(closeUrl);
        } catch { /* server-side auth/session enforcement is the backstop */ }
      }
    };

    window.addEventListener("beforeunload", closeRepracticeWindow);
    window.addEventListener("pagehide", closeRepracticeWindow);
    return () => {
      window.removeEventListener("beforeunload", closeRepracticeWindow);
      window.removeEventListener("pagehide", closeRepracticeWindow);
      closeRepracticeWindow();
    };
  }, [location.pathname, qaPreview]);

  // The setup page title block sits outside the legacy Upload component's
  // existing <main>. Give that introductory block its own named landmark so
  // assistive technologies can navigate it without nesting another <main>.
  useEffect(() => {
    if (location.pathname !== "/upload") return undefined;
    const region = document.querySelector(".interview-setup-shell > .max-w-3xl");
    const heading = region?.querySelector("h1");
    if (!region || !heading) return undefined;

    if (!heading.id) heading.id = "interview-session-setup-title";
    region.setAttribute("role", "region");
    region.setAttribute("aria-labelledby", heading.id);

    return () => {
      region.removeAttribute("role");
      region.removeAttribute("aria-labelledby");
    };
  }, [location.pathname]);

  const showGlobalCopyright = location.pathname !== "/login";

  return (
    <>
      <SessionAllowanceTracker />
      <InterviewLifecycleControls />
      <WarmupPlaybackEnhancer />
      <QuestionTranslationAid />
      <GlobalHelpAccess />
      <GlobalVolumeControl />
      <HelpPanel />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/change-password" element={<ProtectedRoute allow={["/change-password", "/consent", "/setup", "/waiting-beta"]}><ChangePassword /></ProtectedRoute>} />
        <Route path="/consent" element={<ProtectedRoute allow={["/consent", "/setup", "/waiting-beta"]}><GoogleConsent /></ProtectedRoute>} />
        <Route path="/setup" element={<ProtectedRoute allow={["/setup", "/waiting-beta"]}><ProfileSetup /></ProtectedRoute>} />
        <Route path="/waiting-beta" element={<ProtectedRoute allow={["/waiting-beta"]}><WaitingBeta /></ProtectedRoute>} />
        <Route path="/" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />
        <Route path="/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
        <Route path="/upload" element={<ProtectedRoute><Upload /></ProtectedRoute>} />
        <Route path="/prepare/:sessionId" element={<ProtectedRoute><Prepare /></ProtectedRoute>} />
        <Route path="/interview/:sessionId" element={<ProtectedRoute><Interview /></ProtectedRoute>} />
        <Route path="/report/:sessionId" element={<ProtectedRoute><ReportAccessGate /></ProtectedRoute>} />
        <Route path="/free-complete" element={<ProtectedRoute><FreeComplete /></ProtectedRoute>} />
        <Route path="/downloads" element={<ProtectedRoute><Downloads /></ProtectedRoute>} />
        <Route path="/beta-feedback/:sessionId" element={<ProtectedRoute><BetaFeedback /></ProtectedRoute>} />
        <Route path="/beta-report/:sessionId" element={<ProtectedRoute><BetaReport /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminHome /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/standard-questions" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminStandardQuestions /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/responsive-preview" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminResponsivePreview /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/beta-codes" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminBetaCodes /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/beta-feedback" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminBetaFeedback /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/beta-reports" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminBetaReports /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminUsers /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/audit" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminAudit /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/prep-failures" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminPrepFailures /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/bulk-audit" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminBulkAudit /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/privacy-audit" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminPrivacyAudit /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/voice-check" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminVoiceCheck /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/localization-audit" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminLocalizationAudit /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/test-script/:sessionId" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminTestScript /></AdminOnly></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {showGlobalCopyright && (
        <footer
          className="w-full px-4 py-5 text-[10px] sm:text-[11px] text-center text-[color:var(--text-secondary)]"
          data-testid="global-copyright"
        >
          ©2026 Language Masters Software
        </footer>
      )}
    </>
  );
}

export default function App() {
  const skipLaunch = typeof window !== "undefined" && (
    window.location.pathname === "/auth/callback" ||
    /^\/(?:prepare|interview)\/[^/]+\/?$/.test(window.location.pathname)
  );
  const [launchComplete, setLaunchComplete] = useState(skipLaunch);

  if (!launchComplete) {
    return <LaunchScreen onComplete={() => setLaunchComplete(true)} />;
  }

  return (
    <div className="App">
      <EnvironmentBanner />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}
